/** XeAgents.ai**/
import { useQuery, useMutation } from "@tanstack/react-query";
import { queryClient, apiRequest } from "@/lib/queryClient";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useToast } from "@/hooks/use-toast";
import { useState, useEffect, useCallback } from "react";
import type { ReactNode } from "react";
import { Save, Loader2, AlertCircle, RefreshCw, CheckCircle2, XCircle, Phone, PhoneCall, Mic, Pencil, Key, Brain, Eye, EyeOff, Calendar, AudioLines } from "lucide-react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { InfoTooltip } from "@/components/ui/info-tooltip";
import { useTranslation } from "react-i18next";
import BrandingSettings from "./BrandingSettings";
import SMTPSettings from "./SMTPSettings";
import RAGSettings from "./RAGSettings";
import ConversationalLLMSettings from "./ConversationalLLMSettings";
import VoiceLLMSettings from "./VoiceLLMSettings";
import HostAppsSettings from "./HostAppsSettings";

interface LlmModel {
  modelId: string;
  name: string;
  provider: string;
  description?: string;
}

interface LlmProvider {
  id: string;
  name: string;
  icon: string;
}

interface Settings {
  default_llm_provider: string;
  default_llm_free: string;
  default_voice_provider: string;
  nova_sonic_model_id?: string;
  nova_sonic_dual_purpose_mode?: boolean;
  pro_plan_bonus_credits: number;
  credit_price_per_minute: number;
  min_credit_purchase: number;
  twilio_account_sid: string;
  twilio_auth_token: string;
  twilio_api_key: string;
  twilio_api_secret: string;
  twilio_twiml_app_sid: string;
  twilio_configured: boolean;
  elevenlabs_configured: boolean;
  openai_api_key: string;
  openai_configured: boolean;
  anthropic_api_key: string;
  anthropic_configured: boolean;
  google_ai_api_key: string;
  google_ai_configured: boolean;
  google_api_key: string;
  google_configured: boolean;
  azure_openai_endpoint: string;
  azure_openai_api_key: string;
  azure_openai_deployment: string;
  azure_openai_api_version: string;
  use_azure_openai: boolean;
  azure_openai_configured: boolean;
  invoice_prefix: string;
  invoice_start_number: number;
  // Landing page voice demo
  elevenlabs_demo_agent_id: string;
  live_agent_phone_number: string;
  live_agent_phone_number_india: string;
  // Plivo (India telephony)
  plivo_auth_id: string;
  plivo_auth_token: string;
  plivo_configured: boolean;
  // Gemini Live (AI Voice)
  gemini_api_key: string;
  gemini_configured: boolean;
  // Microsoft Azure AD / Teams Calendar Integration (App credentials only - user email is per-tenant)
  azure_ad_client_id: string;
  azure_ad_client_secret: string;
  azure_ad_tenant_id: string;
  azure_ad_configured: boolean;
  // Google Calendar / Meet Integration
  google_calendar_client_id: string;
  google_calendar_client_secret: string;
  google_calendar_configured: boolean;
}

interface ConnectionStatus {
  connected: boolean;
  error?: string;
  details?: string;
  loading?: boolean;
}

interface GlobalSettingsProps {
  onSwitchTab?: (tab: string) => void;
}

/**
 * Sanitize error messages to prevent displaying raw HTML or overly technical errors
 * Returns a clean, user-friendly error message
 */
function sanitizeErrorMessage(error: string | undefined, fallbackMessage: string): string | undefined {
  if (!error) return undefined;
  
  // Detect HTML content (like 502 Bad Gateway pages)
  if (error.includes('<html') || error.includes('<!DOCTYPE') || error.includes('<head>') || error.includes('<body>')) {
    return 'Service temporarily unavailable. Please check your server configuration.';
  }
  
  // Detect JSON parsing errors (indicates server returned non-JSON)
  if (error.includes('Unexpected token') || error.includes('JSON')) {
    return 'Service temporarily unavailable. Please check your server configuration.';
  }
  
  // Detect network/connection errors
  if (error.includes('Failed to fetch') || error.includes('NetworkError') || error.includes('ECONNREFUSED')) {
    return 'Unable to connect to server. Please check if the service is running.';
  }
  
  // If error is too long (likely contains debug info), truncate it
  if (error.length > 200) {
    return fallbackMessage;
  }
  
  return error;
}

// The Master tab is one form (one Save button) shown a section at a time. Hidden sections stay mounted, so edits in
// one section survive a trip to another.
const SETTINGS_SECTIONS = [
  { id: "general", label: "Branding & Email" },
  { id: "telephony", label: "Telephony" },
  { id: "voice", label: "Voice" },
  { id: "ai", label: "AI & LLM" },
  { id: "integrations", label: "Integrations" },
  { id: "billing", label: "Plans & Pricing" },
] as const;
type SectionId = (typeof SETTINGS_SECTIONS)[number]["id"];
const SECTION_STORAGE_KEY = "adminSettingsSection";

function Sec({ id, active, children }: { id: SectionId; active: SectionId; children: ReactNode }) {
  return (
    <div hidden={id !== active} className="space-y-6">
      {children}
    </div>
  );
}

export default function GlobalSettings({ onSwitchTab }: GlobalSettingsProps) {
  const { t } = useTranslation();
  const { toast } = useToast();
  const [formData, setFormData] = useState<Partial<Settings>>({});
  const [hasChanges, setHasChanges] = useState(false);
  
  const [twilioStatus, setTwilioStatus] = useState<ConnectionStatus>({ connected: false, loading: true });
  const [elevenLabsStatus, setElevenLabsStatus] = useState<ConnectionStatus>({ connected: false, loading: true });
  const [googleStatus, setGoogleStatus] = useState<ConnectionStatus>({ connected: false, loading: true });
  
  // LLM Provider state
  const [selectedLlmProvider, setSelectedLlmProvider] = useState<string>("");
  
  // Visibility state for Twilio credential fields
  const [showTwilioFields, setShowTwilioFields] = useState({
    account_sid: false,
    auth_token: false,
    api_key: false,
    api_secret: false,
    twiml_app_sid: false,
  });

  // Visibility state for Plivo credential fields
  const [showPlivoFields, setShowPlivoFields] = useState({
    auth_id: false,
    auth_token: false,
  });

  // Visibility state for Azure AD (Calendar) credential fields
  const [showAzureAdFields, setShowAzureAdFields] = useState({
    client_id: false,
    client_secret: false,
    tenant_id: false,
  });

  // Visibility state for Google Calendar credential fields
  const [showGoogleCalendarFields, setShowGoogleCalendarFields] = useState({
    client_id: false,
    client_secret: false,
  });

  // ElevenLabs API key state
  const [elevenLabsApiKey, setElevenLabsApiKey] = useState("");
  const [showElevenLabsApiKey, setShowElevenLabsApiKey] = useState(false);
  const [isSavingElevenLabsKey, setIsSavingElevenLabsKey] = useState(false);

  // Connection status for calendar integrations
  const [azureAdStatus, setAzureAdStatus] = useState<ConnectionStatus>({ connected: false, loading: false });
  const [googleCalendarStatus, setGoogleCalendarStatus] = useState<ConnectionStatus>({ connected: false, loading: false });

  const [section, setSection] = useState<SectionId>(() => {
    try {
      const saved = sessionStorage.getItem(SECTION_STORAGE_KEY);
      return SETTINGS_SECTIONS.find((x) => x.id === saved)?.id ?? "general";
    } catch {
      return "general";
    }
  });
  const pickSection = (id: SectionId) => {
    setSection(id);
    try {
      sessionStorage.setItem(SECTION_STORAGE_KEY, id);
    } catch {
      /* storage can be blocked; the choice just is not remembered */
    }
  };
  const showAndScroll = (id: SectionId, elementId: string) => {
    pickSection(id);
    setTimeout(() => document.getElementById(elementId)?.scrollIntoView({ behavior: 'smooth', block: 'center' }), 50);
  };

  const scrollToTwilio = () => showAndScroll("telephony", "twilio-credentials");

  const scrollToGoogle = () => showAndScroll("integrations", "google-credentials");

  const { data: settings, isLoading } = useQuery<Settings>({
    queryKey: ["/api/admin/settings"]
  });

  // Fetch LLM models dynamically from provider API based on selected provider
  const { data: providerModels = [], isLoading: isLoadingModels, error: modelsError } = useQuery<LlmModel[]>({
    queryKey: ["/api/admin/llm-models/provider", selectedLlmProvider],
    queryFn: async () => {
      if (!selectedLlmProvider) return [];
      const response = await apiRequest("GET", `/api/admin/llm-models/provider/${selectedLlmProvider}`);
      return response.json();
    },
    enabled: !!selectedLlmProvider
  });

  const testTwilioConnection = useCallback(async () => {
    setTwilioStatus(prev => ({ ...prev, loading: true }));
    try {
      const response = await apiRequest("POST", "/api/admin/test-connection/twilio");
      const data = await response.json();
      const sanitizedError = sanitizeErrorMessage(data.error, 'Twilio credentials not configured');
      setTwilioStatus({ connected: data.connected, error: sanitizedError, loading: false });
    } catch (error: any) {
      const sanitizedError = sanitizeErrorMessage(error.message, 'Connection test failed');
      setTwilioStatus({ connected: false, error: sanitizedError || "Connection test failed", loading: false });
    }
  }, []);

  const testElevenLabsConnection = useCallback(async () => {
    setElevenLabsStatus(prev => ({ ...prev, loading: true }));
    try {
      const response = await apiRequest("POST", "/api/admin/test-connection/elevenlabs");
      const data = await response.json();
      const sanitizedError = sanitizeErrorMessage(data.error, 'ElevenLabs API key not configured');
      setElevenLabsStatus({ connected: data.connected, error: sanitizedError, loading: false });
    } catch (error: any) {
      const sanitizedError = sanitizeErrorMessage(error.message, 'Connection test failed');
      setElevenLabsStatus({ connected: false, error: sanitizedError || "Connection test failed", loading: false });
    }
  }, []);


  const testGoogleConnection = useCallback(async () => {
    setGoogleStatus(prev => ({ ...prev, loading: true }));
    try {
      const response = await apiRequest("POST", "/api/admin/test-connection/google");
      const data = await response.json();
      const sanitizedError = sanitizeErrorMessage(data.error, 'Google API key not configured');
      setGoogleStatus({ connected: data.connected, error: sanitizedError, loading: false });
    } catch (error: any) {
      const sanitizedError = sanitizeErrorMessage(error.message, 'Connection test failed');
      setGoogleStatus({ connected: false, error: sanitizedError || "Connection test failed", loading: false });
    }
  }, []);

  const testAzureAdConnection = useCallback(async () => {
    setAzureAdStatus(prev => ({ ...prev, loading: true }));
    try {
      const response = await apiRequest("POST", "/api/admin/test-connection/azure-ad-calendar");
      const data = await response.json();
      const sanitizedError = sanitizeErrorMessage(data.error, 'Azure AD credentials not configured');
      setAzureAdStatus({ connected: data.connected, error: sanitizedError, loading: false });
    } catch (error: any) {
      const sanitizedError = sanitizeErrorMessage(error.message, 'Connection test failed');
      setAzureAdStatus({ connected: false, error: sanitizedError || "Connection test failed", loading: false });
    }
  }, []);

  const testGoogleCalendarConnection = useCallback(async () => {
    setGoogleCalendarStatus(prev => ({ ...prev, loading: true }));
    try {
      const response = await apiRequest("POST", "/api/admin/test-connection/google-calendar");
      const data = await response.json();
      const sanitizedError = sanitizeErrorMessage(data.error, 'Google Calendar credentials not configured');
      setGoogleCalendarStatus({ connected: data.connected, error: sanitizedError, loading: false });
    } catch (error: any) {
      const sanitizedError = sanitizeErrorMessage(error.message, 'Connection test failed');
      setGoogleCalendarStatus({ connected: false, error: sanitizedError || "Connection test failed", loading: false });
    }
  }, []);

  const testAllConnections = useCallback(() => {
    testTwilioConnection();
    testElevenLabsConnection();
    testGoogleConnection();
  }, [testTwilioConnection, testElevenLabsConnection, testGoogleConnection]);

  // Save ElevenLabs API key to both global settings and the pool
  const saveElevenLabsApiKey = useCallback(async () => {
    if (!elevenLabsApiKey.trim()) {
      toast({
        variant: "destructive",
        title: t("common.error") || "Error",
        description: "Please enter an ElevenLabs API key",
      });
      return;
    }

    setIsSavingElevenLabsKey(true);
    try {
      // First, test the API key
      const testResponse = await apiRequest("POST", "/api/admin/elevenlabs-pool/test", { apiKey: elevenLabsApiKey });
      const testResult = await testResponse.json();

      if (!testResult.valid) {
        toast({
          variant: "destructive",
          title: "Invalid API Key",
          description: "The ElevenLabs API key is invalid. Please check and try again.",
        });
        setIsSavingElevenLabsKey(false);
        return;
      }

      // Save to global settings
      await apiRequest("PATCH", "/api/admin/settings/elevenlabs_api_key", { value: elevenLabsApiKey });

      // Check if pool already has a credential - if so, update it; otherwise add new
      const poolResponse = await apiRequest("GET", "/api/admin/elevenlabs-pool");
      const poolCredentials = await poolResponse.json();

      if (poolCredentials && poolCredentials.length > 0) {
        // Update existing credential
        await apiRequest("PATCH", `/api/admin/elevenlabs-pool/${poolCredentials[0].id}/api-key`, {
          apiKey: elevenLabsApiKey,
        });
      } else {
        // Add new credential to pool
        await apiRequest("POST", "/api/admin/elevenlabs-pool", {
          name: "Primary API Key",
          apiKey: elevenLabsApiKey,
          maxConcurrency: 30,
        });
      }

      // Invalidate queries to refresh status
      queryClient.invalidateQueries({ queryKey: ["/api/admin/settings"] });
      queryClient.invalidateQueries({ queryKey: ["/api/admin/elevenlabs-pool"] });
      queryClient.invalidateQueries({ queryKey: ["/api/admin/elevenlabs-pool/stats"] });

      // Refresh connection status
      testElevenLabsConnection();

      toast({
        title: t("common.success") || "Success",
        description: "ElevenLabs API key saved successfully",
      });

      // Clear the input field
      setElevenLabsApiKey("");
      setShowElevenLabsApiKey(false);
    } catch (error: any) {
      console.error("Error saving ElevenLabs API key:", error);
      toast({
        variant: "destructive",
        title: t("common.error") || "Error",
        description: error.message || "Failed to save ElevenLabs API key",
      });
    } finally {
      setIsSavingElevenLabsKey(false);
    }
  }, [elevenLabsApiKey, toast, t, testElevenLabsConnection]);

  useEffect(() => {
    if (settings) {
      // Initialize formData with defaults for any missing fields
      setFormData({
        ...settings,
        // Ensure LLM provider fields are always defined (empty string if not set)
        openai_api_key: settings.openai_api_key || '',
        anthropic_api_key: settings.anthropic_api_key || '',
        google_ai_api_key: settings.google_ai_api_key || '',
        google_api_key: settings.google_api_key || '',
        default_llm_free: settings.default_llm_free || '',
        nova_sonic_model_id: settings.nova_sonic_model_id || 'amazon.nova-2-5-sonic',
        // Ensure Azure OpenAI fields are properly loaded
        azure_openai_endpoint: settings.azure_openai_endpoint || '',
        azure_openai_api_key: settings.azure_openai_api_key || '',
        azure_openai_deployment: settings.azure_openai_deployment || '',
        azure_openai_api_version: settings.azure_openai_api_version || '2024-10-01-preview',
        use_azure_openai: settings.use_azure_openai || false,
        // Landing page voice demo
        elevenlabs_demo_agent_id: settings.elevenlabs_demo_agent_id || '',
        live_agent_phone_number: settings.live_agent_phone_number || '',
        live_agent_phone_number_india: settings.live_agent_phone_number_india || '',
        // Plivo (India telephony)
        plivo_auth_id: settings.plivo_auth_id || '',
        plivo_auth_token: settings.plivo_auth_token || '',
        // Gemini Live (AI Voice)
        gemini_api_key: settings.gemini_api_key || '',
        // Microsoft Azure AD / Teams Calendar Integration (App credentials only)
        azure_ad_client_id: settings.azure_ad_client_id || '',
        azure_ad_client_secret: settings.azure_ad_client_secret || '',
        azure_ad_tenant_id: settings.azure_ad_tenant_id || '',
        // Google Calendar / Meet Integration
        google_calendar_client_id: settings.google_calendar_client_id || '',
        google_calendar_client_secret: settings.google_calendar_client_secret || '',
      });
      testAllConnections();
    }
  }, [settings, testAllConnections]);

  const updateSetting = useMutation({
    mutationFn: async ({ key, value }: { key: string; value: any }) => {
      return apiRequest("PATCH", `/api/admin/settings/${key}`, { value });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/admin/settings"] });
    },
    onError: (error: any) => {
      toast({
        title: t("admin.settings.updateFailed"),
        description: error.message,
        variant: "destructive"
      });
    }
  });

  const handleChange = (key: keyof Settings, value: any) => {
    setFormData({
      ...formData,
      [key]: value
    });
    setHasChanges(true);
  };

  const handleSave = async () => {
    const promises = Object.keys(formData).map((key) => {
      if (
        key !== 'twilio_configured' &&
        key !== 'plivo_configured' &&
        key !== 'elevenlabs_configured' &&
        key !== 'openai_configured' &&
        key !== 'anthropic_configured' &&
        key !== 'google_ai_configured' &&
        key !== 'google_configured' &&
        key !== 'azure_openai_configured' &&
        key !== 'openai_api_key' &&
        key !== 'anthropic_api_key' &&
        key !== 'google_ai_api_key' &&
        !key.startsWith('conversational_llm_')
      ) {
        const typedKey = key as keyof Settings;
        const currentValue = formData[typedKey];
        
        // Skip if value hasn't changed from settings
        if (currentValue === settings?.[typedKey]) {
          return Promise.resolve();
        }
        
        // Skip password fields if they're still showing masked values (********)
        if ((typedKey === 'twilio_auth_token' ||
             typedKey === 'plivo_auth_token' ||
             typedKey === 'openai_api_key' ||
             typedKey === 'anthropic_api_key' ||
             typedKey === 'google_ai_api_key' ||
             typedKey === 'google_api_key' ||
             typedKey === 'gemini_api_key') &&
            typeof currentValue === 'string' &&
            currentValue.match(/^\*+$/)) {
          return Promise.resolve();
        }
        
        return updateSetting.mutateAsync({
          key,
          value: currentValue
        });
      }
      return Promise.resolve();
    });

    try {
      await Promise.all(promises);
      toast({ title: t("admin.settings.settingsUpdated") });
      setHasChanges(false);
    } catch (error) {
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center p-8">
        <Loader2 className="h-8 w-8 animate-spin" />
      </div>
    );
  }

  const isAnyLoading = twilioStatus.loading || elevenLabsStatus.loading || googleStatus.loading;

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between flex-wrap gap-4">
        <div>
          <h2 className="text-2xl font-bold">{t("admin.settings.title")}</h2>
          <p className="text-muted-foreground">
            {t("admin.settings.description")}
          </p>
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={testAllConnections}
          disabled={isAnyLoading}
          data-testid="button-refresh-connections"
        >
          {isAnyLoading ? (
            <Loader2 className="h-4 w-4 mr-2 animate-spin" />
          ) : (
            <RefreshCw className="h-4 w-4 mr-2" />
          )}
          {t("admin.settings.connectionStatus.refreshAll") || "Refresh Status"}
        </Button>
      </div>

      {/* Connection Status Tiles */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {/* Twilio Connection Status */}
        <Card className={`relative overflow-hidden border-2 ${twilioStatus.loading 
          ? 'border-muted bg-gradient-to-br from-muted/5 to-transparent'
          : twilioStatus.connected 
            ? 'border-emerald-500/30 bg-gradient-to-br from-emerald-500/5 to-transparent' 
            : 'border-red-500/30 bg-gradient-to-br from-red-500/5 to-transparent'}`}>
          <div className={`absolute top-0 right-0 w-24 h-24 rounded-full blur-3xl ${
            twilioStatus.loading ? 'bg-muted/20' : twilioStatus.connected ? 'bg-emerald-500/20' : 'bg-red-500/20'
          }`} />
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between flex-wrap gap-2">
              <div className="flex items-center gap-3">
                <div className={`p-3 rounded-xl ${
                  twilioStatus.loading 
                    ? 'bg-muted/10 ring-1 ring-muted/20'
                    : twilioStatus.connected 
                      ? 'bg-emerald-500/10 ring-1 ring-emerald-500/20' 
                      : 'bg-red-500/10 ring-1 ring-red-500/20'
                }`}>
                  {twilioStatus.loading ? (
                    <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
                  ) : (
                    <Phone className={`h-6 w-6 ${
                      twilioStatus.connected ? 'text-emerald-500' : 'text-red-500'
                    }`} />
                  )}
                </div>
                <div>
                  <CardTitle className="text-lg">{t("admin.settings.twilio.title") || "Twilio API Credentials"}</CardTitle>
                  <CardDescription className="text-sm">
                    {t("admin.settings.connectionStatus.telephony") || "Voice & SMS telephony"}
                  </CardDescription>
                </div>
              </div>
              <div className={`flex items-center gap-2 px-3 py-1.5 rounded-full font-medium text-sm ${
                twilioStatus.loading
                  ? 'bg-muted/10 text-muted-foreground ring-1 ring-muted/30'
                  : twilioStatus.connected
                    ? 'bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 ring-1 ring-emerald-500/30'
                    : 'bg-red-500/10 text-red-600 dark:text-red-400 ring-1 ring-red-500/30'
              }`}>
                {twilioStatus.loading ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" />
                    <span>{t("admin.settings.connectionStatus.checking") || "Checking..."}</span>
                  </>
                ) : twilioStatus.connected ? (
                  <>
                    <CheckCircle2 className="h-4 w-4" />
                    <span>{t("admin.settings.connectionStatus.connected") || "Connected"}</span>
                  </>
                ) : (
                  <>
                    <XCircle className="h-4 w-4" />
                    <span>{t("admin.settings.connectionStatus.disconnected") || "Disconnected"}</span>
                  </>
                )}
              </div>
            </div>
          </CardHeader>
          <CardContent className="pt-2">
            <div className="flex items-center justify-between gap-4">
              <p className="text-sm text-muted-foreground">
                {twilioStatus.loading 
                  ? (t("admin.settings.connectionStatus.verifying") || "Verifying API connection...")
                  : twilioStatus.connected 
                    ? (t("admin.settings.connectionStatus.twilioReady") || "Twilio is configured and ready for calls")
                    : twilioStatus.error || (t("admin.settings.connectionStatus.twilioSetup") || "Configure Twilio credentials below to enable calling")}
              </p>
              <Button 
                variant="outline" 
                size="sm" 
                onClick={scrollToTwilio}
                className="shrink-0"
                data-testid="button-edit-twilio"
              >
                <Pencil className="h-4 w-4 mr-2" />
                {t("admin.settings.connectionStatus.edit") || "Edit"}
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* ElevenLabs Connection Status */}
        <Card className={`relative overflow-hidden border-2 ${elevenLabsStatus.loading 
          ? 'border-muted bg-gradient-to-br from-muted/5 to-transparent'
          : elevenLabsStatus.connected 
            ? 'border-emerald-500/30 bg-gradient-to-br from-emerald-500/5 to-transparent' 
            : 'border-red-500/30 bg-gradient-to-br from-red-500/5 to-transparent'}`}>
          <div className={`absolute top-0 right-0 w-24 h-24 rounded-full blur-3xl ${
            elevenLabsStatus.loading ? 'bg-muted/20' : elevenLabsStatus.connected ? 'bg-emerald-500/20' : 'bg-red-500/20'
          }`} />
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between flex-wrap gap-2">
              <div className="flex items-center gap-3">
                <div className={`p-3 rounded-xl ${
                  elevenLabsStatus.loading 
                    ? 'bg-muted/10 ring-1 ring-muted/20'
                    : elevenLabsStatus.connected 
                      ? 'bg-emerald-500/10 ring-1 ring-emerald-500/20' 
                      : 'bg-red-500/10 ring-1 ring-red-500/20'
                }`}>
                  {elevenLabsStatus.loading ? (
                    <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
                  ) : (
                    <Mic className={`h-6 w-6 ${
                      elevenLabsStatus.connected ? 'text-emerald-500' : 'text-red-500'
                    }`} />
                  )}
                </div>
                <div>
                  <CardTitle className="text-lg">{t("admin.settings.elevenlabs.title") || "ElevenLabs"}</CardTitle>
                  <CardDescription className="text-sm">
                    {t("admin.settings.connectionStatus.aiVoice") || "AI voice synthesis"}
                  </CardDescription>
                </div>
              </div>
              <div className={`flex items-center gap-2 px-3 py-1.5 rounded-full font-medium text-sm ${
                elevenLabsStatus.loading
                  ? 'bg-muted/10 text-muted-foreground ring-1 ring-muted/30'
                  : elevenLabsStatus.connected
                    ? 'bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 ring-1 ring-emerald-500/30'
                    : 'bg-red-500/10 text-red-600 dark:text-red-400 ring-1 ring-red-500/30'
              }`}>
                {elevenLabsStatus.loading ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" />
                    <span>{t("admin.settings.connectionStatus.checking") || "Checking..."}</span>
                  </>
                ) : elevenLabsStatus.connected ? (
                  <>
                    <CheckCircle2 className="h-4 w-4" />
                    <span>{t("admin.settings.connectionStatus.connected") || "Connected"}</span>
                  </>
                ) : (
                  <>
                    <XCircle className="h-4 w-4" />
                    <span>{t("admin.settings.connectionStatus.disconnected") || "Disconnected"}</span>
                  </>
                )}
              </div>
            </div>
          </CardHeader>
          <CardContent className="pt-2 space-y-4">
            <p className="text-sm text-muted-foreground">
              {elevenLabsStatus.loading
                ? (t("admin.settings.connectionStatus.verifying") || "Verifying API connection...")
                : elevenLabsStatus.connected
                  ? (t("admin.settings.connectionStatus.elevenLabsReady") || "ElevenLabs is configured for AI voice agents")
                  : elevenLabsStatus.error || "Enter your ElevenLabs API key to enable AI voices"}
            </p>

            {/* ElevenLabs API Key Input */}
            <div className="space-y-2">
              <Label htmlFor="elevenlabs_api_key" className="text-sm font-medium flex items-center gap-2">
                <Key className="h-4 w-4" />
                ElevenLabs API Key
              </Label>
              <div className="flex gap-2">
                <div className="relative flex-1">
                  <Input
                    id="elevenlabs_api_key"
                    type={showElevenLabsApiKey ? "text" : "password"}
                    value={elevenLabsApiKey}
                    onChange={(e) => setElevenLabsApiKey(e.target.value)}
                    placeholder={elevenLabsStatus.connected ? "••••••••••••••••••••" : "sk_xxxxxxxxxxxxxxxx"}
                    className="pr-10"
                    data-testid="input-elevenlabs-api-key"
                  />
                  <button
                    type="button"
                    className="absolute right-2 top-1/2 -translate-y-1/2 p-1.5 rounded-md hover:bg-muted text-muted-foreground hover:text-foreground transition-colors"
                    onClick={() => setShowElevenLabsApiKey(!showElevenLabsApiKey)}
                    data-testid="button-toggle-elevenlabs-key"
                  >
                    {showElevenLabsApiKey ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
                <Button
                  onClick={saveElevenLabsApiKey}
                  disabled={isSavingElevenLabsKey || !elevenLabsApiKey.trim()}
                  size="default"
                  data-testid="button-save-elevenlabs-key"
                >
                  {isSavingElevenLabsKey ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <Save className="h-4 w-4" />
                  )}
                  <span className="ml-2">{isSavingElevenLabsKey ? "Saving..." : "Save"}</span>
                </Button>
              </div>
              <p className="text-xs text-muted-foreground">
                {elevenLabsStatus.connected
                  ? "API key is configured. Enter a new key to replace it."
                  : "Get your API key from elevenlabs.io/app/settings/api-keys"}
              </p>
            </div>

            {/* Advanced Settings Link */}
            <div className="flex justify-end pt-2 border-t">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => onSwitchTab?.("elevenlabs")}
                className="text-muted-foreground hover:text-foreground"
                data-testid="button-manage-elevenlabs-keys"
              >
                <Key className="h-4 w-4 mr-2" />
                Advanced Pool Settings
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Google Maps API Connection Status */}
        <Card className={`relative overflow-hidden border-2 ${googleStatus.loading 
          ? 'border-muted bg-gradient-to-br from-muted/5 to-transparent'
          : googleStatus.connected 
            ? 'border-emerald-500/30 bg-gradient-to-br from-emerald-500/5 to-transparent' 
            : 'border-red-500/30 bg-gradient-to-br from-red-500/5 to-transparent'}`}>
          <div className={`absolute top-0 right-0 w-24 h-24 rounded-full blur-3xl ${
            googleStatus.loading ? 'bg-muted/20' : googleStatus.connected ? 'bg-emerald-500/20' : 'bg-red-500/20'
          }`} />
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between flex-wrap gap-2">
              <div className="flex items-center gap-3">
                <div className={`p-3 rounded-xl ${
                  googleStatus.loading 
                    ? 'bg-muted/10 ring-1 ring-muted/20'
                    : googleStatus.connected 
                      ? 'bg-emerald-500/10 ring-1 ring-emerald-500/20' 
                      : 'bg-red-500/10 ring-1 ring-red-500/20'
                }`}>
                  {googleStatus.loading ? (
                    <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
                  ) : (
                    <svg className={`h-6 w-6 ${googleStatus.connected ? 'text-emerald-500' : 'text-red-500'}`} viewBox="0 0 24 24" fill="currentColor">
                      <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z"/>
                    </svg>
                  )}
                </div>
                <div>
                  <CardTitle className="text-lg">Google Maps API</CardTitle>
                  <CardDescription className="text-sm">
                    Address validation
                  </CardDescription>
                </div>
              </div>
              <div className={`flex items-center gap-2 px-3 py-1.5 rounded-full font-medium text-sm ${
                googleStatus.loading
                  ? 'bg-muted/10 text-muted-foreground ring-1 ring-muted/30'
                  : googleStatus.connected
                    ? 'bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 ring-1 ring-emerald-500/30'
                    : 'bg-red-500/10 text-red-600 dark:text-red-400 ring-1 ring-red-500/30'
              }`}>
                {googleStatus.loading ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" />
                    <span>{t("admin.settings.connectionStatus.checking") || "Checking..."}</span>
                  </>
                ) : googleStatus.connected ? (
                  <>
                    <CheckCircle2 className="h-4 w-4" />
                    <span>{t("admin.settings.connectionStatus.connected") || "Connected"}</span>
                  </>
                ) : (
                  <>
                    <XCircle className="h-4 w-4" />
                    <span>{t("admin.settings.connectionStatus.disconnected") || "Disconnected"}</span>
                  </>
                )}
              </div>
            </div>
          </CardHeader>
          <CardContent className="pt-2">
            <div className="flex items-center justify-between gap-4">
              <p className="text-sm text-muted-foreground">
                {googleStatus.loading 
                  ? (t("admin.settings.connectionStatus.verifying") || "Verifying API connection...")
                  : googleStatus.connected 
                    ? "Google Places API is configured for address validation"
                    : googleStatus.error || "Configure Google API key for address validation"}
              </p>
              <Button 
                variant="outline" 
                size="sm" 
                onClick={scrollToGoogle}
                className="shrink-0"
                data-testid="button-edit-google"
              >
                <Pencil className="h-4 w-4 mr-2" />
                {t("admin.settings.connectionStatus.edit") || "Edit"}
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>

      <div role="tablist" aria-label="Settings sections" className="flex flex-wrap gap-1 rounded-lg border bg-muted p-1" data-testid="settings-section-nav">
        {SETTINGS_SECTIONS.map((x) => (
          <button
            key={x.id}
            type="button"
            role="tab"
            aria-selected={section === x.id}
            onClick={() => pickSection(x.id)}
            data-testid={`settings-section-${x.id}`}
            className={`rounded-md px-3 py-1.5 text-sm font-medium transition ${section === x.id ? 'bg-background shadow-sm' : 'text-muted-foreground hover:bg-background/60'}`}
          >
            {x.label}
          </button>
        ))}
      </div>

      {/* 1. Branding Settings */}
      <Sec id="general" active={section}>
      <BrandingSettings />

      </Sec>
      {/* 2. Twilio API Credentials */}
      <Sec id="telephony" active={section}>
      <Card id="twilio-credentials">
        <CardHeader>
          <CardTitle>{t("admin.settings.twilio.title")}</CardTitle>
          <CardDescription>
            {t("admin.settings.twilio.description")}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <div className="flex items-center">
              <Label>{t("admin.settings.twilio.accountSid")}</Label>
              <InfoTooltip content={t("admin.settings.twilio.accountSidTooltip")} />
            </div>
            <div className="relative">
              <Input
                type={showTwilioFields.account_sid ? "text" : "password"}
                value={formData.twilio_account_sid || ""}
                onChange={(e) => handleChange("twilio_account_sid", e.target.value)}
                placeholder={t("admin.settings.twilio.accountSidPlaceholder")}
                data-testid="input-twilio-sid"
                className="pr-10"
              />
              <button
                type="button"
                className="absolute right-2 top-1/2 -translate-y-1/2 p-1 rounded hover:bg-muted"
                onClick={() => setShowTwilioFields(prev => ({ ...prev, account_sid: !prev.account_sid }))}
              >
                {showTwilioFields.account_sid ? <EyeOff className="h-4 w-4 text-muted-foreground" /> : <Eye className="h-4 w-4 text-muted-foreground" />}
              </button>
            </div>
            <p className="text-xs text-muted-foreground mt-1">
              {t("admin.settings.twilio.accountSidFormat")}
            </p>
          </div>
          <div>
            <div className="flex items-center">
              <Label>{t("admin.settings.twilio.authToken")}</Label>
              <InfoTooltip content={t("admin.settings.twilio.authTokenTooltip")} />
            </div>
            <div className="relative">
              <Input
                type={showTwilioFields.auth_token ? "text" : "password"}
                value={formData.twilio_auth_token || ""}
                onChange={(e) => handleChange("twilio_auth_token", e.target.value)}
                placeholder={t("admin.settings.twilio.authTokenPlaceholder")}
                data-testid="input-twilio-token"
                className="pr-10"
              />
              <button
                type="button"
                className="absolute right-2 top-1/2 -translate-y-1/2 p-1 rounded hover:bg-muted"
                onClick={() => setShowTwilioFields(prev => ({ ...prev, auth_token: !prev.auth_token }))}
              >
                {showTwilioFields.auth_token ? <EyeOff className="h-4 w-4 text-muted-foreground" /> : <Eye className="h-4 w-4 text-muted-foreground" />}
              </button>
            </div>
            <p className="text-xs text-muted-foreground mt-1">
              {t("admin.settings.twilio.authTokenFormat")}
            </p>
          </div>
          
          <Alert className="bg-blue-50 dark:bg-blue-950/20 border-blue-200 dark:border-blue-900">
            <AlertCircle className="h-4 w-4 text-blue-600 dark:text-blue-400" />
            <AlertDescription className="text-sm text-blue-800 dark:text-blue-200">
              <strong>Voice Widget Configuration:</strong> For browser-based calling (voice widget on landing page), you also need to configure the fields below.
            </AlertDescription>
          </Alert>

          <div>
            <div className="flex items-center">
              <Label>Twilio API Key (for Voice Widget)</Label>
              <InfoTooltip content="API Key (starts with SK) - used to generate access tokens for browser calling. Get from Twilio Console → API Keys & Tokens" />
            </div>
            <div className="relative">
              <Input
                type={showTwilioFields.api_key ? "text" : "password"}
                value={formData.twilio_api_key || ""}
                onChange={(e) => handleChange("twilio_api_key", e.target.value)}
                placeholder="SK..."
                data-testid="input-twilio-api-key"
                className="pr-10"
              />
              <button
                type="button"
                className="absolute right-2 top-1/2 -translate-y-1/2 p-1 rounded hover:bg-muted"
                onClick={() => setShowTwilioFields(prev => ({ ...prev, api_key: !prev.api_key }))}
              >
                {showTwilioFields.api_key ? <EyeOff className="h-4 w-4 text-muted-foreground" /> : <Eye className="h-4 w-4 text-muted-foreground" />}
              </button>
            </div>
            <p className="text-xs text-muted-foreground mt-1">
              Get from <a href="https://console.twilio.com/us1/account/keys-credentials/api-keys" target="_blank" rel="noopener noreferrer" className="underline">Twilio API Keys</a> (NOT your Auth Token)
            </p>
          </div>

          <div>
            <div className="flex items-center">
              <Label>Twilio API Secret</Label>
              <InfoTooltip content="API Secret corresponding to your API Key - shown only once when creating the API Key" />
            </div>
            <div className="relative">
              <Input
                type={showTwilioFields.api_secret ? "text" : "password"}
                value={formData.twilio_api_secret || ""}
                onChange={(e) => handleChange("twilio_api_secret", e.target.value)}
                placeholder="Enter API secret"
                data-testid="input-twilio-api-secret"
                className="pr-10"
              />
              <button
                type="button"
                className="absolute right-2 top-1/2 -translate-y-1/2 p-1 rounded hover:bg-muted"
                onClick={() => setShowTwilioFields(prev => ({ ...prev, api_secret: !prev.api_secret }))}
              >
                {showTwilioFields.api_secret ? <EyeOff className="h-4 w-4 text-muted-foreground" /> : <Eye className="h-4 w-4 text-muted-foreground" />}
              </button>
            </div>
            <p className="text-xs text-muted-foreground mt-1">
              The secret shown when you created the API Key
            </p>
          </div>

          <div>
            <div className="flex items-center">
              <Label>TwiML App SID</Label>
              <InfoTooltip content="TwiML Application SID (starts with AP) - used for voice widget browser calling" />
            </div>
            <div className="relative">
              <Input
                type={showTwilioFields.twiml_app_sid ? "text" : "password"}
                value={formData.twilio_twiml_app_sid || ""}
                onChange={(e) => handleChange("twilio_twiml_app_sid", e.target.value)}
                placeholder="AP..."
                data-testid="input-twilio-twiml-app-sid"
                className="pr-10"
              />
              <button
                type="button"
                className="absolute right-2 top-1/2 -translate-y-1/2 p-1 rounded hover:bg-muted"
                onClick={() => setShowTwilioFields(prev => ({ ...prev, twiml_app_sid: !prev.twiml_app_sid }))}
              >
                {showTwilioFields.twiml_app_sid ? <EyeOff className="h-4 w-4 text-muted-foreground" /> : <Eye className="h-4 w-4 text-muted-foreground" />}
              </button>
            </div>
            <p className="text-xs text-muted-foreground mt-1">
              Get from <a href="https://console.twilio.com/us1/develop/voice/manage/twiml-apps" target="_blank" rel="noopener noreferrer" className="underline">Twilio TwiML Apps</a> → Your App → SID
            </p>
          </div>

          {settings?.twilio_configured && (
            <Alert>
              <AlertCircle className="h-4 w-4" />
              <AlertDescription className="text-sm text-green-600">
                {t("admin.settings.twilio.configured")}
              </AlertDescription>
            </Alert>
          )}

          {/* Dedicated Save Button for Twilio Credentials */}
          <div className="flex justify-end pt-2">
            <Button
              onClick={async () => {
                try {
                  const twilioPromises = [];
                  
                  // Save Twilio Account SID if changed
                  if (formData.twilio_account_sid !== settings?.twilio_account_sid && 
                      formData.twilio_account_sid && 
                      !formData.twilio_account_sid.match(/^\*+$/)) {
                    twilioPromises.push(
                      updateSetting.mutateAsync({
                        key: 'twilio_account_sid',
                        value: formData.twilio_account_sid
                      })
                    );
                  }
                  
                  // Save Twilio Auth Token if changed
                  if (formData.twilio_auth_token !== settings?.twilio_auth_token && 
                      formData.twilio_auth_token && 
                      !formData.twilio_auth_token.match(/^\*+$/)) {
                    twilioPromises.push(
                      updateSetting.mutateAsync({
                        key: 'twilio_auth_token',
                        value: formData.twilio_auth_token
                      })
                    );
                  }
                  
                  // Save Twilio API Key if changed
                  if (formData.twilio_api_key !== settings?.twilio_api_key && 
                      formData.twilio_api_key && 
                      !formData.twilio_api_key.match(/^\*+$/)) {
                    twilioPromises.push(
                      updateSetting.mutateAsync({
                        key: 'twilio_api_key',
                        value: formData.twilio_api_key
                      })
                    );
                  }
                  
                  // Save Twilio API Secret if changed
                  if (formData.twilio_api_secret !== settings?.twilio_api_secret && 
                      formData.twilio_api_secret && 
                      !formData.twilio_api_secret.match(/^\*+$/)) {
                    twilioPromises.push(
                      updateSetting.mutateAsync({
                        key: 'twilio_api_secret',
                        value: formData.twilio_api_secret
                      })
                    );
                  }
                  
                  // Save TwiML App SID if changed
                  if (formData.twilio_twiml_app_sid !== settings?.twilio_twiml_app_sid && 
                      formData.twilio_twiml_app_sid && 
                      !formData.twilio_twiml_app_sid.match(/^\*+$/)) {
                    twilioPromises.push(
                      updateSetting.mutateAsync({
                        key: 'twilio_twiml_app_sid',
                        value: formData.twilio_twiml_app_sid
                      })
                    );
                  }
                  
                  if (twilioPromises.length > 0) {
                    await Promise.all(twilioPromises);
                    // Re-test Twilio connection after saving
                    await testTwilioConnection();
                    toast({ 
                      title: "Twilio Credentials Saved",
                      description: "Twilio settings have been updated successfully"
                    });
                  } else {
                    toast({ 
                      title: "No Changes",
                      description: "Twilio credentials are unchanged",
                      variant: "default"
                    });
                  }
                } catch (error: any) {
                  toast({
                    title: "Failed to Save",
                    description: error.message || "Could not save Twilio credentials",
                    variant: "destructive"
                  });
                }
              }}
              disabled={updateSetting.isPending}
              data-testid="button-save-twilio"
            >
              {updateSetting.isPending ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Saving...
                </>
              ) : (
                <>
                  <Save className="mr-2 h-4 w-4" />
                  Save Twilio Config
                </>
              )}
            </Button>
          </div>
        </CardContent>
      </Card>

      </Sec>
      {/* 2.5. Plivo API Credentials (India Telephony) */}
      <Sec id="telephony" active={section}>
      <Card id="plivo-credentials">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <svg className="h-5 w-5 text-green-500" viewBox="0 0 24 24" fill="currentColor">
              <path d="M6.62 10.79c1.44 2.83 3.76 5.14 6.59 6.59l2.2-2.2c.27-.27.67-.36 1.02-.24 1.12.37 2.33.57 3.57.57.55 0 1 .45 1 1V20c0 .55-.45 1-1 1-9.39 0-17-7.61-17-17 0-.55.45-1 1-1h3.5c.55 0 1 .45 1 1 0 1.25.2 2.45.57 3.57.11.35.03.74-.25 1.02l-2.2 2.2z"/>
            </svg>
            Plivo API Credentials (India)
          </CardTitle>
          <CardDescription>
            Configure Plivo credentials for telephony services in India. Calls placed on Plivo numbers are spoken by Amazon Nova Sonic.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <div className="flex items-center">
              <Label>Plivo Auth ID</Label>
              <InfoTooltip content="Your Plivo Auth ID - get from Plivo Console" />
            </div>
            <div className="relative">
              <Input
                type={showPlivoFields.auth_id ? "text" : "password"}
                value={formData.plivo_auth_id || ""}
                onChange={(e) => handleChange("plivo_auth_id", e.target.value)}
                placeholder="Enter Plivo Auth ID"
                data-testid="input-plivo-auth-id"
                className="pr-10"
              />
              <button
                type="button"
                className="absolute right-2 top-1/2 -translate-y-1/2 p-1 rounded hover:bg-muted"
                onClick={() => setShowPlivoFields(prev => ({ ...prev, auth_id: !prev.auth_id }))}
              >
                {showPlivoFields.auth_id ? <EyeOff className="h-4 w-4 text-muted-foreground" /> : <Eye className="h-4 w-4 text-muted-foreground" />}
              </button>
            </div>
            <p className="text-xs text-muted-foreground mt-1">
              Get from <a href="https://console.plivo.com/dashboard/" target="_blank" rel="noopener noreferrer" className="underline">Plivo Console</a>
            </p>
          </div>

          <div>
            <div className="flex items-center">
              <Label>Plivo Auth Token</Label>
              <InfoTooltip content="Your Plivo Auth Token - get from Plivo Console" />
            </div>
            <div className="relative">
              <Input
                type={showPlivoFields.auth_token ? "text" : "password"}
                value={formData.plivo_auth_token || ""}
                onChange={(e) => handleChange("plivo_auth_token", e.target.value)}
                placeholder="Enter Plivo Auth Token"
                data-testid="input-plivo-auth-token"
                className="pr-10"
              />
              <button
                type="button"
                className="absolute right-2 top-1/2 -translate-y-1/2 p-1 rounded hover:bg-muted"
                onClick={() => setShowPlivoFields(prev => ({ ...prev, auth_token: !prev.auth_token }))}
              >
                {showPlivoFields.auth_token ? <EyeOff className="h-4 w-4 text-muted-foreground" /> : <Eye className="h-4 w-4 text-muted-foreground" />}
              </button>
            </div>
            <p className="text-xs text-muted-foreground mt-1">
              Secret token for API authentication
            </p>
          </div>

          {settings?.plivo_configured && (
            <Alert className="bg-green-50 dark:bg-green-950/20 border-green-200 dark:border-green-900">
              <AlertCircle className="h-4 w-4 text-green-600 dark:text-green-400" />
              <AlertDescription className="text-sm text-green-800 dark:text-green-200">
                Plivo is configured and ready for India telephony
              </AlertDescription>
            </Alert>
          )}

          <Alert className="bg-green-50 dark:bg-green-950/20 border-green-200 dark:border-green-900">
            <AlertCircle className="h-4 w-4 text-green-600 dark:text-green-400" />
            <AlertDescription className="text-sm text-green-800 dark:text-green-200">
              <strong>Note:</strong> Plivo is the telephony carrier for India; Amazon Nova Sonic speaks on those calls.
            </AlertDescription>
          </Alert>

          {/* Dedicated Save Button for Plivo Credentials */}
          <div className="flex justify-end pt-2">
            <Button
              onClick={async () => {
                try {
                  const plivoPromises = [];

                  // Save Plivo Auth ID if changed
                  if (formData.plivo_auth_id !== settings?.plivo_auth_id &&
                      formData.plivo_auth_id &&
                      !formData.plivo_auth_id.match(/^\*+$/)) {
                    plivoPromises.push(
                      updateSetting.mutateAsync({
                        key: 'plivo_auth_id',
                        value: formData.plivo_auth_id
                      })
                    );
                  }

                  // Save Plivo Auth Token if changed
                  if (formData.plivo_auth_token !== settings?.plivo_auth_token &&
                      formData.plivo_auth_token &&
                      !formData.plivo_auth_token.match(/^\*+$/)) {
                    plivoPromises.push(
                      updateSetting.mutateAsync({
                        key: 'plivo_auth_token',
                        value: formData.plivo_auth_token
                      })
                    );
                  }

                  if (plivoPromises.length > 0) {
                    await Promise.all(plivoPromises);
                    toast({
                      title: "Plivo Credentials Saved",
                      description: "Plivo settings have been updated successfully"
                    });
                  } else {
                    toast({
                      title: "No Changes",
                      description: "Plivo credentials are unchanged",
                      variant: "default"
                    });
                  }
                } catch (error: any) {
                  toast({
                    title: "Failed to Save",
                    description: error.message || "Could not save Plivo credentials",
                    variant: "destructive"
                  });
                }
              }}
              disabled={updateSetting.isPending}
              data-testid="button-save-plivo"
            >
              {updateSetting.isPending ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Saving...
                </>
              ) : (
                <>
                  <Save className="mr-2 h-4 w-4" />
                  Save Plivo Config
                </>
              )}
            </Button>
          </div>
        </CardContent>
      </Card>

      </Sec>
      {/* 4.5. Conversational LLM Settings */}
      <Sec id="ai" active={section}>
      <ConversationalLLMSettings />

      </Sec>
      {/* 4.6. Voice LLM Settings (ElevenLabs) */}
      <Sec id="voice" active={section}>
      <VoiceLLMSettings />

      </Sec>
      {/* 5. Google Maps API Credentials */}
      <Sec id="integrations" active={section}>
      <HostAppsSettings />
      <Card id="google-credentials">
        <CardHeader>
          <CardTitle>Google Maps API Key</CardTitle>
          <CardDescription>
            Configure Google Maps API key for address validation in phone number purchase
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <div className="flex items-center">
              <Label>Google API Key</Label>
              <InfoTooltip content="API key from Google Cloud Console with Maps JavaScript API and Places API enabled" />
            </div>
            <Input
              type="password"
              value={formData.google_api_key || ""}
              onChange={(e) => handleChange("google_api_key", e.target.value)}
              placeholder="AIzaSy..."
              data-testid="input-google-key"
            />
            <p className="text-xs text-muted-foreground mt-1">
              Get your API key from <a href="https://console.cloud.google.com/apis/credentials" target="_blank" rel="noopener noreferrer" className="underline">Google Cloud Console</a>
            </p>
          </div>
          {settings?.google_configured && (
            <Alert>
              <AlertCircle className="h-4 w-4" />
              <AlertDescription className="text-sm text-green-600">
                Google Maps API is configured and ready
              </AlertDescription>
            </Alert>
          )}
        </CardContent>
      </Card>

      </Sec>
      {/* 5.5. Voice Provider Selection */}
      <Sec id="voice" active={section}>
      <Card id="voice-provider-selection">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Mic className="h-5 w-5" />
            Default Voice Provider
          </CardTitle>
          <CardDescription>
            Select the voice engine for AI agent calls. Amazon Nova Sonic is the default; choose ElevenLabs to run calls on ElevenLabs instead.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <div className="flex items-center mb-2">
              <Label>Voice Provider</Label>
              <InfoTooltip content="Choose the voice engine for calls: Amazon Nova Sonic (default) or ElevenLabs. Nova Sonic speaks for Nova Sonic agents and for ElevenLabs flow agents on Plivo numbers whose flow it can run." />
            </div>
            {(() => {
              // Two engines, one choice. Nova Sonic is the default; "bulbul" and any unknown value mean the ElevenLabs path.
              const engine = formData.default_voice_provider === "elevenlabs" || formData.default_voice_provider === "bulbul" ? "elevenlabs" : "nova-sonic";
              const options = [
                {
                  value: "nova-sonic",
                  label: "Amazon Nova Sonic",
                  hint: "Speech-to-speech AI. Hindi, Indian English and 6 more languages. Default.",
                  icon: <AudioLines className="h-5 w-5 text-orange-500 shrink-0" />,
                },
                {
                  value: "elevenlabs",
                  label: "ElevenLabs",
                  hint: "High-quality AI voice synthesis with its own voice library.",
                  icon: <Mic className="h-5 w-5 text-purple-500 shrink-0" />,
                },
              ];
              return (
                <div
                  role="radiogroup"
                  aria-label="Voice engine"
                  className="grid grid-cols-1 gap-1 rounded-lg border bg-muted p-1 sm:grid-cols-2"
                  data-testid="toggle-voice-provider"
                >
                  {options.map((option) => {
                    const active = engine === option.value;
                    return (
                      <button
                        key={option.value}
                        type="button"
                        role="radio"
                        aria-checked={active}
                        onClick={() => handleChange("default_voice_provider" as keyof Settings, option.value)}
                        data-testid={`toggle-voice-provider-${option.value}`}
                        className={`flex items-center gap-3 rounded-md p-3 text-left transition ${
                          active ? "bg-background shadow-sm ring-1 ring-primary" : "hover:bg-background/60"
                        }`}
                      >
                        {option.icon}
                        <span>
                          <span className="flex items-center gap-2 font-medium">
                            {option.label}
                            {active && <span className="rounded bg-primary/10 px-1.5 py-0.5 text-[10px] font-semibold uppercase text-primary">Active</span>}
                          </span>
                          <span className="block text-xs text-muted-foreground">{option.hint}</span>
                        </span>
                      </button>
                    );
                  })}
                </div>
              );
            })()}
            <p className="text-xs text-muted-foreground mt-2">
              Click to switch, then press Save. New calls use the chosen engine within seconds; calls already in progress are not affected.
            </p>
          </div>

          <Alert className="bg-blue-50 dark:bg-blue-950/20 border-blue-200 dark:border-blue-900">
            <AlertCircle className="h-4 w-4 text-blue-600 dark:text-blue-400" />
            <AlertDescription className="text-sm text-blue-800 dark:text-blue-200">
              <strong>Note:</strong> With Amazon Nova Sonic selected, calls through Plivo numbers use Nova Sonic and not ElevenLabs:
              for agents created as Nova Sonic agents, and for ElevenLabs flow agents whose flow uses only steps Nova Sonic supports
              (a flow with a webhook or other step Nova Sonic cannot run stays on ElevenLabs). Twilio numbers, prompt-only agents and agents set to a regional language Nova Sonic does not speak (Tamil, Telugu, Bengali, Marathi...) always use ElevenLabs.
              Switching back to ElevenLabs takes effect within seconds and needs no deploy.
            </AlertDescription>
          </Alert>
        </CardContent>
      </Card>

      {/* Landing Page Voice Demo */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <PhoneCall className="h-5 w-5" />
            Landing Page Voice Widget
          </CardTitle>
          <CardDescription>
            Configure the floating "Talk to AI" call button on the public landing page. Visitors can directly call your AI agent via their browser.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <div className="flex items-center">
              <Label>Voice Widget Phone Number</Label>
              <InfoTooltip content="The phone number that visitors will call when clicking the voice widget. This should be your incoming AI agent's phone number (e.g., 18303544852). Enter only numeric digits - the + prefix will be added automatically." />
            </div>
            <Input
              type="text"
              value={formData.live_agent_phone_number || ""}
              onChange={(e) => {
                // Only allow numeric characters
                const numericValue = e.target.value.replace(/[^0-9]/g, '');
                handleChange("live_agent_phone_number" as keyof Settings, numericValue);
              }}
              placeholder="e.g. 18303544852"
              data-testid="input-live-agent-phone-number"
              pattern="[0-9]*"
              inputMode="numeric"
            />
            <p className="text-xs text-muted-foreground mt-1">
              Enter only numeric digits (e.g., 18303544852). The + prefix will be added automatically. This number is used by the homepage voice widget.
            </p>
          </div>

          {/* India-specific phone number */}
          <div>
            <div className="flex items-center">
              <Label>🇮🇳 India Phone Number (Optional)</Label>
              <InfoTooltip content="An India-specific phone number for visitors from India. When a visitor from India uses the voice widget, this number will be dialed instead of the default. Auto-detected via browser geolocation." />
            </div>
            <Input
              type="text"
              value={formData.live_agent_phone_number_india || ""}
              onChange={(e) => {
                const numericValue = e.target.value.replace(/[^0-9]/g, '');
                handleChange("live_agent_phone_number_india" as keyof Settings, numericValue);
              }}
              placeholder="e.g. 911141170734"
              data-testid="input-live-agent-phone-number-india"
              pattern="[0-9]*"
              inputMode="numeric"
            />
            <p className="text-xs text-muted-foreground mt-1">
              Enter an Indian phone number (e.g., 911141170734). The + prefix will be added automatically. If left empty, Indian visitors will use the default number above.
            </p>
          </div>

          <Alert className="bg-blue-50 dark:bg-blue-950/20 border-blue-200 dark:border-blue-900">
            <AlertCircle className="h-4 w-4 text-blue-600 dark:text-blue-400" />
            <AlertDescription className="text-sm text-blue-800 dark:text-blue-200">
              <strong>Important:</strong> Both phone numbers must be assigned to incoming AI agents in your Agents page. The voice widget auto-detects the visitor&apos;s country and routes to the appropriate number.
            </AlertDescription>
          </Alert>

          <div className="flex justify-end pt-2">
            <Button
              onClick={async () => {
                try {
                  const promises = [];
                  promises.push(updateSetting.mutateAsync({
                    key: 'live_agent_phone_number',
                    value: formData.live_agent_phone_number,
                  }));
                  promises.push(updateSetting.mutateAsync({
                    key: 'live_agent_phone_number_india',
                    value: formData.live_agent_phone_number_india || '',
                  }));
                  await Promise.all(promises);
                  toast({
                    title: "Voice Widget Configured",
                    description: "Voice widget phone numbers have been updated successfully",
                  });
                } catch (error: any) {
                  toast({
                    title: "Failed to Save",
                    description: error.message || "Could not save phone number",
                    variant: "destructive",
                  });
                }
              }}
              disabled={updateSetting.isPending}
              data-testid="button-save-live-agent-phone"
            >
              {updateSetting.isPending ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Saving...
                </>
              ) : (
                <>
                  <Save className="mr-2 h-4 w-4" />
                  Save Voice Widget Config
                </>
              )}
            </Button>
          </div>
        </CardContent>
      </Card>

      </Sec>
      {/* 6. RAG Configuration */}
      <Sec id="ai" active={section}>
      <RAGSettings />

      </Sec>
      {/* 6.5. Microsoft Azure AD / Teams Calendar Integration */}
      <Sec id="integrations" active={section}>
      <Card id="azure-ad-calendar-credentials">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Calendar className="h-5 w-5 text-blue-500" />
            Microsoft Azure AD / Teams Calendar
          </CardTitle>
          <CardDescription>
            Configure Microsoft Azure AD app credentials for Teams meeting and Outlook calendar integration.
            These are shared credentials for the OAuth app. Each tenant configures their own calendar user email in Appointments Settings.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <div className="flex items-center">
              <Label>Azure Client ID</Label>
              <InfoTooltip content="Application (client) ID from Azure AD app registration. Find it in Azure Portal → App registrations → Your app → Overview" />
            </div>
            <div className="relative">
              <Input
                type={showAzureAdFields.client_id ? "text" : "password"}
                value={formData.azure_ad_client_id || ""}
                onChange={(e) => handleChange("azure_ad_client_id", e.target.value)}
                placeholder="e.g., 67a3d0fa-1903-4002-b604-55a8862b9e09"
                data-testid="input-azure-ad-client-id"
                className="pr-10"
              />
              <button
                type="button"
                className="absolute right-2 top-1/2 -translate-y-1/2 p-1 rounded hover:bg-muted"
                onClick={() => setShowAzureAdFields(prev => ({ ...prev, client_id: !prev.client_id }))}
              >
                {showAzureAdFields.client_id ? <EyeOff className="h-4 w-4 text-muted-foreground" /> : <Eye className="h-4 w-4 text-muted-foreground" />}
              </button>
            </div>
            <p className="text-xs text-muted-foreground mt-1">
              Get from <a href="https://portal.azure.com/#blade/Microsoft_AAD_RegisteredApps/ApplicationsListBlade" target="_blank" rel="noopener noreferrer" className="underline">Azure Portal → App registrations</a>
            </p>
          </div>

          <div>
            <div className="flex items-center">
              <Label>Azure Client Secret</Label>
              <InfoTooltip content="Client secret value from Azure AD app registration. Create it in Azure Portal → App registrations → Your app → Certificates & secrets" />
            </div>
            <div className="relative">
              <Input
                type={showAzureAdFields.client_secret ? "text" : "password"}
                value={formData.azure_ad_client_secret || ""}
                onChange={(e) => handleChange("azure_ad_client_secret", e.target.value)}
                placeholder="Enter Azure Client Secret"
                data-testid="input-azure-ad-client-secret"
                className="pr-10"
              />
              <button
                type="button"
                className="absolute right-2 top-1/2 -translate-y-1/2 p-1 rounded hover:bg-muted"
                onClick={() => setShowAzureAdFields(prev => ({ ...prev, client_secret: !prev.client_secret }))}
              >
                {showAzureAdFields.client_secret ? <EyeOff className="h-4 w-4 text-muted-foreground" /> : <Eye className="h-4 w-4 text-muted-foreground" />}
              </button>
            </div>
            <p className="text-xs text-muted-foreground mt-1">
              Secret value (not the Secret ID). Create a new secret if you don't have one.
            </p>
          </div>

          <div>
            <div className="flex items-center">
              <Label>Azure Tenant ID</Label>
              <InfoTooltip content="Directory (tenant) ID from Azure AD. Find it in Azure Portal → Azure Active Directory → Overview" />
            </div>
            <div className="relative">
              <Input
                type={showAzureAdFields.tenant_id ? "text" : "password"}
                value={formData.azure_ad_tenant_id || ""}
                onChange={(e) => handleChange("azure_ad_tenant_id", e.target.value)}
                placeholder="e.g., c085e663-309b-445c-87cf-dda7147ff529"
                data-testid="input-azure-ad-tenant-id"
                className="pr-10"
              />
              <button
                type="button"
                className="absolute right-2 top-1/2 -translate-y-1/2 p-1 rounded hover:bg-muted"
                onClick={() => setShowAzureAdFields(prev => ({ ...prev, tenant_id: !prev.tenant_id }))}
              >
                {showAzureAdFields.tenant_id ? <EyeOff className="h-4 w-4 text-muted-foreground" /> : <Eye className="h-4 w-4 text-muted-foreground" />}
              </button>
            </div>
            <p className="text-xs text-muted-foreground mt-1">
              Your Azure AD tenant identifier
            </p>
          </div>

          {settings?.azure_ad_configured && (
            <Alert className="bg-green-50 dark:bg-green-950/20 border-green-200 dark:border-green-900">
              <CheckCircle2 className="h-4 w-4 text-green-600 dark:text-green-400" />
              <AlertDescription className="text-sm text-green-800 dark:text-green-200">
                Microsoft Azure AD is configured and ready for Teams meetings
              </AlertDescription>
            </Alert>
          )}

          <Alert className="bg-blue-50 dark:bg-blue-950/20 border-blue-200 dark:border-blue-900">
            <AlertCircle className="h-4 w-4 text-blue-600 dark:text-blue-400" />
            <AlertDescription className="text-sm text-blue-800 dark:text-blue-200">
              <strong>Required Permissions:</strong> Your Azure AD app needs <code>Calendars.ReadWrite</code> and <code>OnlineMeetings.ReadWrite</code> permissions (Application type).
              <br /><br />
              <strong>Note:</strong> Tenants configure their own Microsoft calendar user email in the Appointments Settings page.
            </AlertDescription>
          </Alert>

          {/* Test Connection and Save Button */}
          <div className="flex justify-between pt-2">
            <Button
              variant="outline"
              onClick={testAzureAdConnection}
              disabled={azureAdStatus.loading}
              data-testid="button-test-azure-ad"
            >
              {azureAdStatus.loading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Testing...
                </>
              ) : azureAdStatus.connected ? (
                <>
                  <CheckCircle2 className="mr-2 h-4 w-4 text-green-500" />
                  Connected
                </>
              ) : (
                <>
                  <RefreshCw className="mr-2 h-4 w-4" />
                  Test Connection
                </>
              )}
            </Button>
            <Button
              onClick={async () => {
                try {
                  const azureAdPromises = [];

                  if (formData.azure_ad_client_id !== settings?.azure_ad_client_id &&
                      formData.azure_ad_client_id &&
                      !formData.azure_ad_client_id.match(/^\*+$/)) {
                    azureAdPromises.push(
                      updateSetting.mutateAsync({
                        key: 'azure_ad_client_id',
                        value: formData.azure_ad_client_id
                      })
                    );
                  }

                  if (formData.azure_ad_client_secret !== settings?.azure_ad_client_secret &&
                      formData.azure_ad_client_secret &&
                      !formData.azure_ad_client_secret.match(/^\*+$/)) {
                    azureAdPromises.push(
                      updateSetting.mutateAsync({
                        key: 'azure_ad_client_secret',
                        value: formData.azure_ad_client_secret
                      })
                    );
                  }

                  if (formData.azure_ad_tenant_id !== settings?.azure_ad_tenant_id &&
                      formData.azure_ad_tenant_id &&
                      !formData.azure_ad_tenant_id.match(/^\*+$/)) {
                    azureAdPromises.push(
                      updateSetting.mutateAsync({
                        key: 'azure_ad_tenant_id',
                        value: formData.azure_ad_tenant_id
                      })
                    );
                  }

                  if (azureAdPromises.length > 0) {
                    await Promise.all(azureAdPromises);
                    await testAzureAdConnection();
                    toast({
                      title: "Azure AD Configuration Saved",
                      description: "Microsoft Azure AD settings have been updated successfully"
                    });
                  } else {
                    toast({
                      title: "No Changes",
                      description: "Azure AD credentials are unchanged",
                      variant: "default"
                    });
                  }
                } catch (error: any) {
                  toast({
                    title: "Failed to Save",
                    description: error.message || "Could not save Azure AD credentials",
                    variant: "destructive"
                  });
                }
              }}
              disabled={updateSetting.isPending}
              data-testid="button-save-azure-ad"
            >
              {updateSetting.isPending ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Saving...
                </>
              ) : (
                <>
                  <Save className="mr-2 h-4 w-4" />
                  Save Azure AD Config
                </>
              )}
            </Button>
          </div>
        </CardContent>
      </Card>

      </Sec>
      {/* 6.6. Google Calendar / Meet Integration */}
      <Sec id="integrations" active={section}>
      <Card id="google-calendar-credentials">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <svg className="h-5 w-5" viewBox="0 0 24 24">
              <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
              <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
              <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
              <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
            </svg>
            Google Calendar / Meet
          </CardTitle>
          <CardDescription>
            Configure Google OAuth credentials for Google Calendar and Google Meet integration.
            These enable automatic Google Meet link generation and calendar availability checking.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <div className="flex items-center">
              <Label>Google Client ID</Label>
              <InfoTooltip content="OAuth 2.0 Client ID from Google Cloud Console. Create it in APIs & Services → Credentials → Create Credentials → OAuth client ID" />
            </div>
            <div className="relative">
              <Input
                type={showGoogleCalendarFields.client_id ? "text" : "password"}
                value={formData.google_calendar_client_id || ""}
                onChange={(e) => handleChange("google_calendar_client_id", e.target.value)}
                placeholder="e.g., 613464435646-97leugvfbnashit2ce4iidn9q0qgjdbn.apps.googleusercontent.com"
                data-testid="input-google-calendar-client-id"
                className="pr-10"
              />
              <button
                type="button"
                className="absolute right-2 top-1/2 -translate-y-1/2 p-1 rounded hover:bg-muted"
                onClick={() => setShowGoogleCalendarFields(prev => ({ ...prev, client_id: !prev.client_id }))}
              >
                {showGoogleCalendarFields.client_id ? <EyeOff className="h-4 w-4 text-muted-foreground" /> : <Eye className="h-4 w-4 text-muted-foreground" />}
              </button>
            </div>
            <p className="text-xs text-muted-foreground mt-1">
              Get from <a href="https://console.cloud.google.com/apis/credentials" target="_blank" rel="noopener noreferrer" className="underline">Google Cloud Console → Credentials</a>
            </p>
          </div>

          <div>
            <div className="flex items-center">
              <Label>Google Client Secret</Label>
              <InfoTooltip content="OAuth 2.0 Client Secret from Google Cloud Console. Find it in your OAuth client ID details." />
            </div>
            <div className="relative">
              <Input
                type={showGoogleCalendarFields.client_secret ? "text" : "password"}
                value={formData.google_calendar_client_secret || ""}
                onChange={(e) => handleChange("google_calendar_client_secret", e.target.value)}
                placeholder="Enter Google Client Secret"
                data-testid="input-google-calendar-client-secret"
                className="pr-10"
              />
              <button
                type="button"
                className="absolute right-2 top-1/2 -translate-y-1/2 p-1 rounded hover:bg-muted"
                onClick={() => setShowGoogleCalendarFields(prev => ({ ...prev, client_secret: !prev.client_secret }))}
              >
                {showGoogleCalendarFields.client_secret ? <EyeOff className="h-4 w-4 text-muted-foreground" /> : <Eye className="h-4 w-4 text-muted-foreground" />}
              </button>
            </div>
            <p className="text-xs text-muted-foreground mt-1">
              Client secret value from your OAuth credentials
            </p>
          </div>

          {settings?.google_calendar_configured && (
            <Alert className="bg-green-50 dark:bg-green-950/20 border-green-200 dark:border-green-900">
              <CheckCircle2 className="h-4 w-4 text-green-600 dark:text-green-400" />
              <AlertDescription className="text-sm text-green-800 dark:text-green-200">
                Google Calendar is configured and ready for Google Meet
              </AlertDescription>
            </Alert>
          )}

          <Alert className="bg-blue-50 dark:bg-blue-950/20 border-blue-200 dark:border-blue-900">
            <AlertCircle className="h-4 w-4 text-blue-600 dark:text-blue-400" />
            <AlertDescription className="text-sm text-blue-800 dark:text-blue-200">
              <strong>Required APIs:</strong> Enable Google Calendar API and Google Meet API in your Google Cloud project. Also configure OAuth consent screen.
            </AlertDescription>
          </Alert>

          {/* Test Connection and Save Button */}
          <div className="flex justify-between pt-2">
            <Button
              variant="outline"
              onClick={testGoogleCalendarConnection}
              disabled={googleCalendarStatus.loading}
              data-testid="button-test-google-calendar"
            >
              {googleCalendarStatus.loading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Testing...
                </>
              ) : googleCalendarStatus.connected ? (
                <>
                  <CheckCircle2 className="mr-2 h-4 w-4 text-green-500" />
                  Connected
                </>
              ) : (
                <>
                  <RefreshCw className="mr-2 h-4 w-4" />
                  Test Connection
                </>
              )}
            </Button>
            <Button
              onClick={async () => {
                try {
                  const googleCalendarPromises = [];

                  if (formData.google_calendar_client_id !== settings?.google_calendar_client_id &&
                      formData.google_calendar_client_id &&
                      !formData.google_calendar_client_id.match(/^\*+$/)) {
                    googleCalendarPromises.push(
                      updateSetting.mutateAsync({
                        key: 'google_calendar_client_id',
                        value: formData.google_calendar_client_id
                      })
                    );
                  }

                  if (formData.google_calendar_client_secret !== settings?.google_calendar_client_secret &&
                      formData.google_calendar_client_secret &&
                      !formData.google_calendar_client_secret.match(/^\*+$/)) {
                    googleCalendarPromises.push(
                      updateSetting.mutateAsync({
                        key: 'google_calendar_client_secret',
                        value: formData.google_calendar_client_secret
                      })
                    );
                  }

                  if (googleCalendarPromises.length > 0) {
                    await Promise.all(googleCalendarPromises);
                    await testGoogleCalendarConnection();
                    toast({
                      title: "Google Calendar Configuration Saved",
                      description: "Google Calendar settings have been updated successfully"
                    });
                  } else {
                    toast({
                      title: "No Changes",
                      description: "Google Calendar credentials are unchanged",
                      variant: "default"
                    });
                  }
                } catch (error: any) {
                  toast({
                    title: "Failed to Save",
                    description: error.message || "Could not save Google Calendar credentials",
                    variant: "destructive"
                  });
                }
              }}
              disabled={updateSetting.isPending}
              data-testid="button-save-google-calendar"
            >
              {updateSetting.isPending ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Saving...
                </>
              ) : (
                <>
                  <Save className="mr-2 h-4 w-4" />
                  Save Google Calendar Config
                </>
              )}
            </Button>
          </div>
        </CardContent>
      </Card>

      </Sec>
      {/* 7. SMTP Email Settings */}
      <Sec id="general" active={section}>
      <SMTPSettings />

      </Sec>
      {/* 8-11. Plan Settings, Pricing, System Resources, System Tools */}
      <Sec id="billing" active={section}>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* 9. Plan Settings */}
        <Card>
          <CardHeader>
            <CardTitle>{t("admin.settings.planSettings.title")}</CardTitle>
            <CardDescription>
              {t("admin.settings.planSettings.description")}
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <div className="flex items-center">
                <Label>{t("admin.settings.planSettings.bonusCredits")}</Label>
                <InfoTooltip content={t("admin.settings.planSettings.bonusCreditsTooltip")} />
              </div>
              <Input
                type="number"
                value={formData.pro_plan_bonus_credits || 0}
                onChange={(e) => handleChange("pro_plan_bonus_credits", parseInt(e.target.value, 10) || 0)}
                data-testid="input-bonus-credits"
              />
              <p className="text-xs text-muted-foreground mt-1">
                {t("admin.settings.planSettings.bonusCreditsHint")}
              </p>
            </div>
          </CardContent>
        </Card>

        {/* 10. Pricing Settings - only Credit Price, Phone Monthly Cost, Min Purchase */}
        <Card>
          <CardHeader>
            <CardTitle>{t("admin.settings.pricing.title")}</CardTitle>
            <CardDescription>
              {t("admin.settings.pricing.description")}
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <div className="flex items-center">
                <Label>{t("admin.settings.pricing.creditPrice")}</Label>
                <InfoTooltip content={t("admin.settings.pricing.creditPriceTooltip")} />
              </div>
              <Input
                type="number"
                step="0.01"
                value={formData.credit_price_per_minute || 0}
                onChange={(e) => handleChange("credit_price_per_minute", parseFloat(e.target.value) || 0)}
                data-testid="input-credit-price"
              />
              <p className="text-xs text-muted-foreground mt-1">
                {t("admin.settings.pricing.creditPriceHint")}
              </p>
            </div>
            <div>
              <div className="flex items-center">
                <Label>{t("admin.settings.pricing.minPurchase")}</Label>
                <InfoTooltip content={t("admin.settings.pricing.minPurchaseTooltip")} />
              </div>
              <Input
                type="number"
                value={formData.min_credit_purchase || 10}
                onChange={(e) => handleChange("min_credit_purchase", parseInt(e.target.value, 10) || 10)}
                data-testid="input-min-purchase"
              />
            </div>
          </CardContent>
        </Card>

        {/* 11. Invoice Settings */}
        <Card>
          <CardHeader>
            <CardTitle>Invoice Settings</CardTitle>
            <CardDescription>
              Configure invoice numbering format and series
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <div className="flex items-center">
                <Label>Invoice Prefix</Label>
                <InfoTooltip content="Prefix for invoice numbers (e.g., 'INV' creates INV-2025-00001)" />
              </div>
              <Input
                value={formData.invoice_prefix || "INV"}
                onChange={(e) => handleChange("invoice_prefix", e.target.value.toUpperCase())}
                placeholder="INV"
                data-testid="input-invoice-prefix"
              />
              <p className="text-xs text-muted-foreground mt-1">
                Format: PREFIX-YEAR-NUMBER (e.g., INV-2025-00001)
              </p>
            </div>
            <div>
              <div className="flex items-center">
                <Label>Starting Number</Label>
                <InfoTooltip content="Starting number for the invoice series. Leave at 1 to continue from existing invoices." />
              </div>
              <Input
                type="number"
                min="1"
                value={formData.invoice_start_number || 1}
                onChange={(e) => handleChange("invoice_start_number", parseInt(e.target.value, 10) || 1)}
                data-testid="input-invoice-start"
              />
              <p className="text-xs text-muted-foreground mt-1">
                Used only when no invoices exist for the current year
              </p>
            </div>
          </CardContent>
        </Card>

      </div>

      </Sec>
      <div className="flex justify-end">
        <Button
          onClick={handleSave}
          disabled={!hasChanges || updateSetting.isPending}
          data-testid="button-save-settings"
        >
          {updateSetting.isPending ? (
            <>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              {t("admin.settings.saving")}
            </>
          ) : (
            <>
              <Save className="mr-2 h-4 w-4" />
              {t("admin.settings.saveSettings")}
            </>
          )}
        </Button>
      </div>
    </div>
  );
}
